/**
 * Block 10 (2026-05-27): Shared LimitCheckBadge component.
 *
 * Дві модальності:
 * - 'compact' — для widget на HomePage: маленький rect із кольоровим
 *   border-left, org name + %, click → drill-down.
 * - 'full' — для FopReportsPage tab: повна форма з progress bar +
 *   деталями + warning message + recommended_action.
 *
 * Refactor: вилучення з inline LimitCheckTab/LimitCheckCard у
 * FopReportsPage:208-268 (Block 8.3 acceptance regression-safe).
 */
import type { AnnualLimitCheck, WarningLevel } from '@/api/fop_reports';
import {
  WARNING_COLOR,
  WARNING_LABEL_UA,
} from '@/shared/warning_colors';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

interface Props {
  data: AnnualLimitCheck;
  mode?: 'compact' | 'full';
  onClick?: () => void;
}

export default function LimitCheckBadge({ data, mode = 'full', onClick }: Props) {
  const color = WARNING_COLOR[data.warning_level as WarningLevel];
  const pct = Math.min(Number(data.usage_percent), 100);

  if (mode === 'compact') {
    return (
      <div
        data-testid="limit-check-badge-compact"
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        style={{
          padding: '0.6rem 0.75rem',
          background: 'var(--bg-panel)',
          borderRadius: 6,
          borderLeft: `4px solid ${color}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          cursor: onClick ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ fontSize: '0.9rem' }}>{data.organization_name}</strong>
          <span style={{ color, fontWeight: 600, fontSize: '0.9rem' }}>
            {fmt(data.usage_percent)}%
          </span>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', gap: 8 }}>
          <span style={{ color }}>{WARNING_LABEL_UA[data.warning_level as WarningLevel]}</span>
          <span>• Залишок: {fmt(data.remaining_amount)} грн</span>
        </div>
      </div>
    );
  }

  // Full mode — повна форма (раніше LimitCheckTab у FopReportsPage).
  return (
    <div data-testid="limit-check-badge-full">
      <h3>Контроль річного ліміту єдинника</h3>
      <div style={{ marginBottom: 12 }}>
        <strong>Використано: {fmt(data.usage_percent)}%</strong>
        <div
          style={{
            background: 'var(--bg-soft)',
            height: 24,
            marginTop: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: color,
              transition: 'width 0.3s',
            }}
          />
        </div>
      </div>

      <p>
        Ліміт (snapshot 01.01): <strong>{fmt(data.annual_limit)} грн</strong>
      </p>
      <p>Дохід YTD: {fmt(data.revenue_ytd)} грн</p>
      <p>
        Залишок: <strong>{fmt(data.remaining_amount)} грн</strong>
      </p>

      <div
        style={{
          padding: 12,
          margin: '12px 0',
          background: color + '20',
          border: `1px solid ${color}`,
        }}
      >
        <strong style={{ color }}>{data.warning_level}: </strong>
        {data.warning_message}
        {data.recommended_action && (
          <p style={{ marginTop: 6 }}>
            <strong>Рекомендована дія: </strong>
            {data.recommended_action}
          </p>
        )}
      </div>

      {Object.keys(data.excluded_breakdown).length > 0 && (
        <details>
          <summary>Виключено з доходу (ПКУ ст.292.11)</summary>
          <ul>
            {Object.entries(data.excluded_breakdown).map(([reason, amount]) => (
              <li key={reason}>
                {reason}: {fmt(amount)} грн
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
